import { Navigate } from "react-router-dom";
import {
  ADMIN_SETTING_PAGE_ACCOUNTING_MANAGEMENT,
  ADMIN_SETTING_PAGE_CALENDAR_MANAGEMENT,
  ADMIN_SETTING_PAGE_CITY_MANAGEMENT,
  ADMIN_SETTING_PAGE_DOCUMENT_MANAGEMENT,
  ADMIN_SETTING_PAGE_MILESTONE_MANAGEMENT,
  ADMIN_SETTING_PAGE_ORDER_MANAGEMENT,
  ADMIN_SETTING_PAGE_PROFILE_MANAGEMENT,
  ADMIN_SETTING_PAGE_RELATIONSHIP_MANAGEMENT,
  ADMIN_SETTING_PAGE_USER_MANAGEMENT,
  ENTITY_NAMES,
  PERMISSIONS,
} from "@miplatform/constants";
import {
  ContactRelationshipList,
  ManageCities,
  ManageContactRelationships,
} from "../../views/AdminSettings";
import { AdminSettings } from "../../views/AdminSettings/AdminSettings";
import { ContactDetails } from "../../views/AdminSettings/ContactDetails/ContactDetails";
import { ImportQuickBooksCustomers } from "../../views/AdminSettings/ImportQuickBooksCustomers/ImportQuickBooksCustomers";
import { AssignmentTemplateDetails } from "../../views/AdminSettings/ManageAssignmentTemplate/AssignmentTemplateDetails/AssignmentTemplateDetails";
import { AssignmentTemplateList } from "../../views/AdminSettings/ManageAssignmentTemplate/AssignmentTemplateList/AssignmentTemplateList";
import { CityDetails } from "../../views/AdminSettings/ManageCities/CityDetails/CityDetails";
import { ManageContact } from "../../views/AdminSettings/ManageContact/ManageContact";
import { ManageDocumentCategory } from "../../views/AdminSettings/ManageDocumentCategory/ManageDocumentCategory";
import {
  InspectionTemplate,
  ManageInspectionTemplate,
} from "../../views/AdminSettings/ManageInspectionTemplate";
import { IssueTemplateDetails } from "../../views/AdminSettings/ManageIssueTemplate/IssueTemplateDetails/IssueTemplateDetails";
import { IssueTemplateList } from "../../views/AdminSettings/ManageIssueTemplate/IssueTemplateList/IssueTemplateList";
import { ManageLaborRates } from "../../views/AdminSettings/ManageLaborRates/ManageLaborRates";
import { ManageMyAccount } from "../../views/AdminSettings/ManageMyAccount/ManageMyAccount";
import { ManageOrderCardFields } from "../../views/AdminSettings/ManageOrderCardFields/ManageOrderCardFields";
import { ManageOrderReasons } from "../../views/AdminSettings/ManageOrderReasons/ManageOrderReasons";
import { ManageOrderTemplate } from "../../views/AdminSettings/ManageOrderTemplate/ManageOrderTemplate";
import { OrderTemplate } from "../../views/AdminSettings/ManageOrderTemplate/OrderTemplate/OrderTemplate";
import { ManageOrderTemplateCapacity } from "../../views/AdminSettings/ManageProductCapacity/ManageOrderTemplateCapacity";
import { ManageQuickBooksIntegration } from "../../views/AdminSettings/ManageQuickBooksIntegration/ManageQuickBooksIntegration";
import { ManageRole } from "../../views/AdminSettings/ManageRole/ManageRole";
import { RoleDetails } from "../../views/AdminSettings/ManageRole/RoleDetails/RoleDetails";
import { ManageTitle } from "../../views/AdminSettings/ManageTitle/ManageTitle";
import { TitleDetails } from "../../views/AdminSettings/ManageTitle/TitleDetails/TitleDetails";
import { ManageUser } from "../../views/AdminSettings/ManageUser/ManageUser";
import { UserDetails } from "../../views/AdminSettings/ManageUser/UserDetails/UserDetails";
import {
  ManageWorkOrderTemplate,
  WorkOrderTemplate,
} from "../../views/AdminSettings/ManageWorkOrderTemplate";
import { BuildTemplate } from "../../views/AdminSettings/MilestoneManagement/BuildTemplate";
import { BuildTemplateDetails } from "../../views/AdminSettings/MilestoneManagement/BuildTemplateDetails/BuildTemplateDetails";
import { ManageMilestones } from "../../views/AdminSettings/MilestoneManagement/ManageMilestones/ManageMilestones";
import { TenantConfigurationSystemAdmin } from "../../views/AdminSettings/SystemAdministration/TenantConfigurationSystemAdmin";
import { ManageTrade } from "../../views/AdminSettings/Trade/ManageTrade";

export const adminSettingsRoutes = [
  {
    path: "/settings",
    name: "admin",
    permissionName: PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION,
    children: [
      {
        index: true,
        element: <Navigate to="user-management" replace />,
      },
      {
        path: "user-management",
        name: "admin",
        exact: true,
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_USER_MANAGEMENT}
            title="User Management"
          />
        ),
        permissionName: [PERMISSIONS.USER.READ, PERMISSIONS.ROLE.READ],
      },
      {
        path: "user-management/users",
        exact: true,
        name: "Users",
        element: <ManageUser />,
        permissionName: PERMISSIONS.USER.READ,
      },
      {
        path: "user-management/users/:id",
        exact: true,
        name: "Users",
        element: <UserDetails />,
        permissionName: PERMISSIONS.USER.READ,
      },
      {
        path: "user-management/contacts",
        exact: true,
        name: "Contacts",
        element: <ManageContact />,
        permissionName: PERMISSIONS.USER.READ,
      },
      {
        path: "contact-management/accounts/:accountId/contacts/:contactId",
        name: "Contact Details",
        element: <ContactDetails />,
      },
      {
        path: "user-management/roles",
        exact: true,
        name: "Users",
        element: <ManageRole />,
        permissionName: PERMISSIONS.ROLE.READ,
      },
      {
        path: "user-management/roles/:id",
        exact: true,
        name: "Users",
        element: <RoleDetails />,
        permissionName: PERMISSIONS.ROLE.UPDATE,
      },
      {
        path: "user-management/titles",
        exact: true,
        name: "Titles",
        element: <ManageTitle />,
        permissionName: PERMISSIONS.ROLE.READ,
      },
      {
        path: "user-management/titles/:id",
        exact: true,
        name: "Titles",
        element: <TitleDetails />,
        permissionName: PERMISSIONS.ROLE.READ,
      },
      {
        path: "user-management/trades",
        exact: true,
        name: "Trades",
        element: <ManageTrade />,
        permissionName: PERMISSIONS.TRADE.READ,
      },
      {
        path: "user-management/trades/:id",
        exact: true,
        name: "Trades",
        element: <ManageTrade />,
        permissionName: PERMISSIONS.TRADE.READ,
      },
      {
        path: "order-management",
        exact: true,
        name: "Order Management",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_ORDER_MANAGEMENT}
            title="Order Management"
          />
        ),
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "document-management/document-categories",
        exact: true,
        name: "Document Categories",
        element: <ManageDocumentCategory />,
        permissionName: [
          PERMISSIONS.DOCUMENT_CATEGORY.CREATE,
          PERMISSIONS.DOCUMENT_CATEGORY.UPDATE,
          PERMISSIONS.DOCUMENT_CATEGORY.DELETE,
        ],
      },
      {
        path: "order-management/order-types",
        exact: true,
        name: `${ENTITY_NAMES.ORDER_TEMPLATES}`,
        element: <ManageOrderTemplate />,
      },
      {
        path: "order-management/order-types/:id",
        exact: true,
        element: <Navigate to="details" replace />,
        permissionName: [PERMISSIONS.ATTEMPT.UPDATE, PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/order-types/:id/:activeTab",
        exact: true,
        name: `${ENTITY_NAMES.ORDER_TEMPLATE} Details`,
        element: <OrderTemplate />,
        permissionName: [PERMISSIONS.ATTEMPT.UPDATE, PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/issue-types",
        exact: true,
        name: `${ENTITY_NAMES.ISSUE_TEMPLATE}`,
        element: <IssueTemplateList />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/issue-types/:id/",
        exact: true,
        element: <Navigate to="details" replace />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/issue-types/:id/:activeTab",
        exact: true,
        name: `${ENTITY_NAMES.ISSUE_TEMPLATE} Details`,
        element: <IssueTemplateDetails />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/order-reasons/",
        exact: true,
        element: <Navigate to="hold-reasons" replace />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/order-reasons/:activeTab",
        name: "Order Reason:activeTab",
        element: <ManageOrderReasons />,
        // TODO: permission will change in the future
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/order-type-capacity",
        exact: true,
        name: `${ENTITY_NAMES.ORDER_TEMPLATE} Capacity`,
        element: <ManageOrderTemplateCapacity />,
      },
      {
        path: "order-management/order-card-fields",
        exact: true,
        name: "Order Card Fields",
        element: <ManageOrderCardFields />,
        permissionName: [PERMISSIONS.LABEL_CONFIG.READ],
      },
      {
        path: "order-management/work-order-types",
        exact: true,
        name: ENTITY_NAMES.WORK_ORDER_TEMPLATES,
        element: <ManageWorkOrderTemplate />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/inspection-types",
        exact: true,
        name: ENTITY_NAMES.INSPECTION_TEMPLATES,
        element: <ManageInspectionTemplate />,
      },
      {
        path: "order-management/work-order-types/:id",
        exact: true,
        element: <Navigate to="details" replace />,
        permissionName: [
          PERMISSIONS.ORDER_TEMPLATE.UPDATE,
          PERMISSIONS.ORDER.READ,
        ],
      },
      {
        path: "order-management/work-order-types/:id/:activeTab",
        exact: true,
        name: `${ENTITY_NAMES.WORK_ORDER_TEMPLATE} Details`,
        element: <WorkOrderTemplate />,
        permissionName: [
          PERMISSIONS.ORDER_TEMPLATE.UPDATE,
          PERMISSIONS.ORDER.READ,
        ],
      },
      {
        path: "order-management/inspection-types/:id/:activeTab",
        exact: true,
        name: `${ENTITY_NAMES.INSPECTION_TEMPLATE} Details`,
        element: <InspectionTemplate />,
        permissionName: [
          PERMISSIONS.INSPECTION_TEMPLATE.UPDATE,
          PERMISSIONS.INSPECTION.READ,
        ],
      },
      {
        path: "accounting-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_ACCOUNTING_MANAGEMENT}
            title="Accounting Management"
          />
        ),
      },
      {
        path: "accounting-management/quickbooks-integration",
        exact: true,
        name: "QuickBooks Integration",
        element: <ManageQuickBooksIntegration />,
      },
      {
        path: "accounting-management/import-quickbooks-customers",
        exact: true,
        name: "Import QuickBooks Customers",
        element: <ImportQuickBooksCustomers />,
      },
      // TODO: Add the permission for the below routes
      {
        path: "city-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_CITY_MANAGEMENT}
            title="City Management"
          />
        ),
        permissionName: [PERMISSIONS.ATTEMPT.UPDATE, PERMISSIONS.ORDER.READ],
      },
      {
        path: "calendar-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_CALENDAR_MANAGEMENT}
            title="Calendar Management"
          />
        ),
        permissionName: [PERMISSIONS.DOCUMENT_CATEGORY.UPDATE],
      },
      {
        path: "calendar-management/labor-rates",
        exact: true,
        name: "Labor Rates",
        element: <ManageLaborRates />,
        permissionName: [PERMISSIONS.CALENDAR_SET.UPDATE],
      },
      {
        path: "document-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_DOCUMENT_MANAGEMENT}
            title="Document Management"
          />
        ),
        permissionName: [PERMISSIONS.DOCUMENT_CATEGORY.UPDATE],
      },
      {
        path: "city-management/cities/:id",
        exact: true,
        name: "Titles",
        element: <CityDetails />,
        permissionName: PERMISSIONS.ROLE.READ,
      },
      {
        path: "city-management/cities",
        exact: true,
        name: "Cities",
        element: <ManageCities />,
      },
      // TODO: Add the permission for the below routes
      {
        path: "relationship-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_RELATIONSHIP_MANAGEMENT}
            title="Relationship Management"
          />
        ),
      },
      {
        path: "relationship-management/contact-relationships",
        exact: true,
        name: "Contacts",
        element: <ManageContactRelationships />,
      },
      {
        path: "relationship-management/contact-relationships/:dependentType",
        exact: true,
        name: "Contacts Relationship List",
        element: <ContactRelationshipList />,
      },
      // TODO: Add the permission for the below routes
      {
        path: "profile-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_PROFILE_MANAGEMENT}
            title="Profile Management"
          />
        ),
      },
      {
        path: "profile-management/my-account",
        exact: true,
        name: "My Account",
        element: <ManageMyAccount />,
      },
      // TODO: Add the permission for the below routes
      {
        path: "system-administration/tenant-configuration",
        exact: true,
        name: "Tenant Configuration",
        element: <TenantConfigurationSystemAdmin />,
        permissionName: PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION,
      },
      {
        path: "milestone-management",
        exact: true,
        name: "admin",
        element: (
          <AdminSettings
            page={ADMIN_SETTING_PAGE_MILESTONE_MANAGEMENT}
            title="Milestone Management"
          />
        ),
      },
      {
        path: "milestone-management/build-types",
        exact: true,
        name: "admin",
        element: <BuildTemplate />,
      },
      {
        path: "milestone-management/manage-milestones",
        exact: true,
        name: "admin",
        element: <ManageMilestones />,
      },
      {
        path: "milestone-management/build-types/:id/",
        exact: true,
        element: <Navigate to="milestones" replace />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "milestone-management/build-types/:id/:activeTab",
        exact: true,
        name: `${ENTITY_NAMES.BUILD_TEMPLATE} Details`,
        element: <BuildTemplateDetails />,
        permissionName: [
          PERMISSIONS.BUILD_TEMPLATE.UPDATE,
          PERMISSIONS.ORDER.READ,
        ],
      },
      {
        path: "order-management/assignment-templates",
        exact: true,
        name: "Assignment Template",
        element: <AssignmentTemplateList />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
      {
        path: "order-management/assignment-templates/:id/:activeTab",
        exact: true,
        name: "Assignment Template Details",
        element: <AssignmentTemplateDetails />,
        permissionName: [PERMISSIONS.ORDER.READ],
      },
    ],
  },
];
